import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, AlertTriangle } from "lucide-react";

export type ComplianceItem = {
  id: number | null;
  name: string | null;
  item: string;
  type?: string | null;
  description?: string | null;
  pdf: string | null;
  frequencyDays: number | null;
  lastCompletionDate: string | null;
  nextDueDate: string | null;
  dueInDays: number | null;
  status: string;
  required: boolean;
  source: string;
  actionPath?: string | null;
  actionLabel?: string | null;
};

type TrainingListControlProps = {
  requiredItems: ComplianceItem[];
  optionalItems: ComplianceItem[];
  onOpen: (item: ComplianceItem) => void;
  header?: React.ReactNode;
  actionRow?: React.ReactNode;
};

const formatDate = (value: string | null) => {
  if (!value) {
    return "Not set";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Not set";
  }

  return parsed.toLocaleDateString();
};

const statusBadge = (item: ComplianceItem) => {
  if (item.status === "PAST_DUE") {
    return <Badge variant="destructive">Past due</Badge>;
  }

  if (item.status === "DUE_SOON") {
    return <Badge variant="outline">Due soon</Badge>;
  }

  if (item.status === "CURRENT") {
    return <Badge variant="secondary">Current</Badge>;
  }

  return <Badge variant="outline">Info</Badge>;
};

export const TrainingListControl: React.FC<TrainingListControlProps> = ({
  requiredItems,
  optionalItems,
  onOpen,
  header,
  actionRow,
}) => {
  return (
    <div className="w-full space-y-4">
      {header}
      <div className="space-y-4">
        {requiredItems.length > 0 || actionRow ? (
          <div className="rounded border border-gray-200 bg-white">
            <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-900">
              Required
            </div>
            <div className="divide-y divide-gray-200">
              {actionRow}
              {requiredItems.map((item) => {
                const dueLabel = item.nextDueDate
                  ? formatDate(item.nextDueDate)
                  : "Not scheduled";
                const isBlocking =
                  item.required &&
                  typeof item.dueInDays === "number" &&
                  item.dueInDays <= 7;
                const badge =
                  item.status === "DUE_SOON" ? (
                    <Badge variant="destructive">Past due</Badge>
                  ) : (
                    statusBadge(item)
                  );
                return (
                  <div
                    key={`${item.item}-${item.id || "req"}`}
                    className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-gray-900 font-medium">
                        <span>{item.name || item.item}</span>
                        {badge}
                        {isBlocking ? (
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                        ) : null}
                      </div>
                      <div className="text-xs text-gray-600">
                        {item.description
                          ? item.description
                          : `Next due: ${dueLabel}${item.frequencyDays ? ` • Every ${item.frequencyDays} days` : ""}`}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        id={`open-${item.item}-pdf`}
                        variant="outline"
                        size="sm"
                        onClick={() => onOpen(item)}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {item.actionLabel || "Open"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {optionalItems.length > 0 ? (
          <div className="rounded border border-gray-200 bg-white">
            <div className="px-4 py-2 border-b border-gray-200 text-sm font-semibold text-gray-900">
              Optional
            </div>
            <div className="divide-y divide-gray-200">
              {optionalItems.map((item) => {
                const dueLabel = item.nextDueDate
                  ? formatDate(item.nextDueDate)
                  : "Not scheduled";
                return (
                  <div
                    key={`${item.item}-${item.id || "opt"}`}
                    className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-gray-900 font-medium">
                        <span>{item.name || item.item}</span>
                        {statusBadge(item)}
                      </div>
                      <div className="text-xs text-gray-600">
                        Next due: {dueLabel}
                        {item.frequencyDays
                          ? ` • Every ${item.frequencyDays} days`
                          : ""}
                      </div>
                      {item.source === "static" ? (
                        <div className="text-xs text-gray-500">
                          Educational
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        id={`open-${item.item}-pdf`}
                        variant="outline"
                        size="sm"
                        onClick={() => onOpen(item)}
                        className="gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Open
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
